import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AUTH_CHANGED_EVENTS, isAuthed } from "./lib/auth";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { usePublicConfig } from "./lib/publicConfig";
import { AppShell } from "./components/AppShell";
import { WorkspaceProvider, useWorkspace } from "./lib/workspace";

const WorkspaceAccessPage = lazy(() => import("./pages/WorkspaceAccessPage").then(m => ({ default: m.WorkspaceAccessPage })));
const WelcomePage = lazy(() => import("./pages/WelcomePage").then(m => ({ default: m.WelcomePage })));

const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage").then((m) => ({ default: m.AuthCallbackPage })));
const LandingPage = lazy(() => import("./pages/LandingPage").then((m) => ({ default: m.LandingPage })));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage").then((m) => ({ default: m.CheckoutPage })));
const GrantsPage = lazy(() => import("./pages/GrantsPage").then((m) => ({ default: m.GrantsPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const MandatoryMfaPage = lazy(() => import("./pages/MandatoryMfaPage").then((m) => ({ default: m.MandatoryMfaPage })));
const SignupPage = lazy(() => import("./pages/SignupPage").then((m) => ({ default: m.SignupPage })));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage").then((m) => ({ default: m.VerifyEmailPage })));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })));
const AcceptInvitePage = lazy(() => import("./pages/AcceptInvitePage").then((m) => ({ default: m.AcceptInvitePage })));
const ServersPage = lazy(() => import("./pages/ServersPage").then((m) => ({ default: m.ServersPage })));
const NewServerPage = lazy(() => import("./pages/NewServerPage").then((m) => ({ default: m.NewServerPage })));
const ServerDetailPage = lazy(() => import("./pages/ServerDetailPage").then((m) => ({ default: m.ServerDetailPage })));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })));
const PricingPage = lazy(() => import("./pages/PricingPage").then((m) => ({ default: m.PricingPage })));
const BillingPage = lazy(() => import("./pages/BillingPage").then((m) => ({ default: m.BillingPage })));
const BillingHomePage = lazy(() => import("./pages/BillingPage").then((m) => ({ default: m.BillingHomePage })));
const OrgsPage = lazy(() => import("./pages/OrgsPage").then((m) => ({ default: m.OrgsPage })));
const OrgDetailPage = lazy(() => import("./pages/OrgDetailPage").then((m) => ({ default: m.OrgDetailPage })));
const SkillsPage = lazy(() => import("./pages/SkillsPage"));
const CapabilitiesPage = lazy(() => import("./pages/CapabilitiesPage").then(m => ({ default: m.CapabilitiesPage })));
const SurfacesPage = lazy(() => import("./pages/SurfacesPage").then(m => ({ default: m.SurfacesPage })));
const ConnectionsPage = lazy(() => import("./pages/ConnectionsPage").then(m => ({ default: m.ConnectionsPage })));
const ApprovalsPage = lazy(() => import("./pages/ApprovalsPage").then(m => ({ default: m.ApprovalsPage })));
const WorkflowsPage = lazy(() => import('./pages/WorkflowsPage').then(m => ({ default: m.WorkflowsPage })));
const DatabaseConnectionsPage = lazy(() => import('./pages/DatabaseConnectionsPage').then(m => ({ default: m.DatabaseConnectionsPage })));
const ExternalConnectionsPage = lazy(() => import('./pages/ExternalConnectionsPage').then(m => ({ default: m.ExternalConnectionsPage })));
const GettingStartedPage = lazy(() => import("./pages/GettingStartedPage").then((m) => ({ default: m.GettingStartedPage })));
const QuickstartPage = lazy(() => import("./pages/docs/QuickstartPage").then((m) => ({ default: m.QuickstartPage })));
const ConceptsPage = lazy(() => import("./pages/docs/ConceptsPage").then((m) => ({ default: m.ConceptsPage })));
const GovernancePage = lazy(() => import('./pages/docs/GovernancePage').then(m => ({ default: m.GovernancePage })));
const ConnectPage = lazy(() => import("./pages/docs/ConnectPage").then((m) => ({ default: m.ConnectPage })));
const CustomToolsPage = lazy(() => import("./pages/docs/CustomToolsPage").then((m) => ({ default: m.CustomToolsPage })));
const SelfHostPage = lazy(() => import("./pages/docs/SelfHostPage").then((m) => ({ default: m.SelfHostPage })));
const ApiReferencePage = lazy(() => import("./pages/docs/ApiReferencePage").then((m) => ({ default: m.ApiReferencePage })));
const CataloguePage = lazy(() => import("./pages/CataloguePage").then((m) => ({ default: m.CataloguePage })));
const CatalogueDetailPage = lazy(() => import("./pages/CataloguePage").then((m) => ({ default: m.CatalogueDetailPage })));
const SecuritySettingsPage = lazy(() => import("./pages/SecuritySettingsPage").then((m) => ({ default: m.SecuritySettingsPage })));
const PlatformAdminPage = lazy(() => import("./pages/PlatformAdminPage").then(m => ({ default: m.PlatformAdminPage })));
const HarnessPage = lazy(() => import('./pages/HarnessPage').then(m => ({ default: m.HarnessPage })));
const HarnessConsentPage = lazy(() => import('./pages/HarnessPage').then(m => ({ default: m.HarnessConsentPage })));
const HarnessReviewPage = lazy(() => import('./pages/HarnessPage').then(m => ({ default: m.HarnessReviewPage })));

// Re-renders whenever setToken/clearToken fires one of AUTH_CHANGED_EVENTS, or a
// different tab mutates localStorage. Without this the route tree keeps its
// initial auth snapshot and an expired 401 doesn't bounce the user to /login.
function useAuthed(): boolean {
    const [authed, setAuthed] = useState<boolean>(() => isAuthed());
    useEffect(() => {
        const update = () => setAuthed(isAuthed());
        for (const event of AUTH_CHANGED_EVENTS) {
            window.addEventListener(event, update);
        }
        window.addEventListener("storage", update);
        return () => {
            for (const event of AUTH_CHANGED_EVENTS) {
                window.removeEventListener(event, update);
            }
            window.removeEventListener("storage", update);
        };
    }, []);
    return authed;
}

function PageFallback() {
    return (
        <div className="flex min-h-[40vh] items-center justify-center px-6 py-10 text-sm text-pugio-muted">
            <span className="mr-3 h-2 w-2 rounded-full bg-pugio-accent pulse" />
            Loading Pugio
        </div>
    );
}

function RequireAuth({ children }: { children: ReactNode }) {
    const authed = useAuthed();
    const location = useLocation();
    if (!authed) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    return <>{children}</>;
}

function RequireHarnessAuth({ children }: { children: ReactNode }) {
    const authed = useAuthed();
    const location = useLocation();
    if (!authed) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    return <>{children}</>;
}

function DashboardLayout() {
    const authed = useAuthed();
    const { workspace, customer } = useWorkspace();
    if (authed) return <AppShell><Outlet key={`${customer?.id}:${workspace?.id}:${workspace?.project_id}`} /></AppShell>;
    return (
        <div className="flex min-h-full flex-col bg-pugio-bg">
            <Navbar />
            <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
                <Outlet />
            </main>
            <Footer />
        </div>
    );
}

function FullBleedLayout() {
    const authed = useAuthed();
    const { workspace, customer } = useWorkspace();
    if (authed) return <AppShell fullBleed><Outlet key={`${customer?.id}:${workspace?.id}:${workspace?.project_id}`} /></AppShell>;
    return (
        <div className="flex min-h-full flex-col bg-pugio-bg">
            <Navbar />
            <main className="flex-1">
                <Outlet />
            </main>
            <Footer />
        </div>
    );
}

function RootRoute() {
    const authed = useAuthed();
    return authed ? <Navigate to="/welcome" replace /> : <LandingPage />;
}

function ReleaseAvailabilityNotice() {
    const { email_delivery_enabled, isolated_runtime_enabled } = usePublicConfig();
    if (email_delivery_enabled !== false && isolated_runtime_enabled !== false) return null;
    return (
        <div role="status" className="border-b border-amber-400/20 bg-amber-400/10 px-4 py-2 text-center text-xs text-pugio-text">
            {email_delivery_enabled === false && <p>Email delivery is temporarily unavailable. Password resets, email verification and emailed invitations are paused.</p>}
            {isolated_runtime_enabled === false && <p>Isolated custom-code execution is unavailable. API integrations and separately enabled database or MCP connections use their own access controls.</p>}
        </div>
    );
}

export function App() {
    return (
        <WorkspaceProvider><Suspense fallback={<PageFallback />}>
        <ReleaseAvailabilityNotice />
        <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            {/* Self-service email-link landing pages.  Standalone (no
                DashboardLayout) because the user is unauthenticated
                when arriving from the email — the /verify-email page
                in particular needs to work BEFORE the user has a
                full session, and /reset-password is reached from a
                "I forgot my password" loop where they're definitely
                signed out. */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            {/* Mandatory MFA enrolment page — entered with an
                mfa_enrollment_only-scoped JWT in isolated storage; the
                page clears it and redirects to /login on success.  Not
                wrapped in DashboardLayout/RequireAuth because the
                restricted JWT isn't a full session. */}
            <Route path="/auth/mfa/enroll-required" element={<MandatoryMfaPage />} />
            {/* End-user grants portal — sig-authed via querystring HMAC.
                Not wrapped in DashboardLayout or RequireAuth because the
                agent's end user is NOT a Pugio customer and shouldn't
                see the authed-customer nav. */}
            <Route path="/grants" element={<GrantsPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route element={<DashboardLayout />}>
                <Route path="/welcome" element={<RequireAuth><WelcomePage /></RequireAuth>} />
                <Route path="/platform" element={<RequireAuth><PlatformAdminPage /></RequireAuth>} />
                <Route path="/harness" element={<RequireHarnessAuth><HarnessPage /></RequireHarnessAuth>} />
                <Route path="/harness/connect" element={<RequireHarnessAuth><HarnessConsentPage /></RequireHarnessAuth>} />
                <Route path="/harness/review/:id" element={<RequireHarnessAuth><HarnessReviewPage /></RequireHarnessAuth>} />
                <Route path="/capability-bundles" element={<RequireAuth><CapabilitiesPage /></RequireAuth>} />
                <Route path="/surfaces" element={<RequireAuth><SurfacesPage /></RequireAuth>} />
                <Route path="/connections" element={<RequireAuth><ConnectionsPage /></RequireAuth>} />
                <Route path="/workflows" element={<RequireAuth><WorkflowsPage /></RequireAuth>} />
                <Route path="/database-connections" element={<RequireAuth><DatabaseConnectionsPage /></RequireAuth>} />
                <Route path="/external-connections" element={<RequireAuth><ExternalConnectionsPage /></RequireAuth>} />
                <Route path="/approvals" element={<RequireAuth><ApprovalsPage /></RequireAuth>} />
                <Route
                    path="/servers"
                    element={
                        <RequireAuth>
                            <ServersPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/servers/new"
                    element={
                        <RequireAuth>
                            <NewServerPage />
                        </RequireAuth>
                    }
                />
<Route path="/orgs/:orgId/access" element={<RequireAuth><WorkspaceAccessPage /></RequireAuth>} />
                <Route
                    path="/orgs"
                    element={
                        <RequireAuth>
                            <OrgsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/orgs/:id"
                    element={
                        <RequireAuth>
                            <OrgDetailPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/billing"
                    element={
                        <RequireAuth>
                            <BillingHomePage />
                        </RequireAuth>
                    }
                />
                <Route path="/payments" element={<Navigate to="/billing" replace />} />
                <Route
                    path="/orgs/:orgId/billing"
                    element={
                        <RequireAuth>
                            <BillingPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/skills"
                    element={
                        <RequireAuth>
                            <SkillsPage />
                        </RequireAuth>
                    }
                />
                <Route
                    path="/settings/security"
                    element={
                        <RequireAuth>
                            <SecuritySettingsPage />
                        </RequireAuth>
                    }
                />
                {/* Docs site — public, unauthenticated.  Each topic is
                    its own .tsx page wrapped in a shared DocsLayout.  The
                    legacy /docs/getting-started URL redirects to the new
                    /docs/quickstart so existing footer links / external
                    references don't 404. */}
                <Route path="/docs" element={<Navigate to="/docs/quickstart" replace />} />
                <Route path="/docs/getting-started" element={<Navigate to="/docs/quickstart" replace />} />
                <Route path="/docs/quickstart" element={<QuickstartPage />} />
                <Route path="/docs/concepts" element={<ConceptsPage />} />
                <Route path="/docs/governance" element={<GovernancePage />} />
                <Route path="/docs/connect" element={<ConnectPage />} />
                <Route path="/docs/custom-tools" element={<CustomToolsPage />} />
                <Route path="/docs/self-host" element={<SelfHostPage />} />
                <Route path="/docs/api-reference" element={<ApiReferencePage />} />
                {/* Old hardcoded JSX page kept reachable at a deep link in
                    case anything still points at it; new content lives at
                    /docs/quickstart. */}
                <Route path="/docs/legacy-getting-started" element={<GettingStartedPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/catalogue" element={<CataloguePage />} />
                <Route path="/catalogue/:slug" element={<CatalogueDetailPage />} />
            </Route>
            <Route element={<FullBleedLayout />}>
                <Route
                    path="/servers/:id"
                    element={
                        <RequireAuth>
                            <ServerDetailPage />
                        </RequireAuth>
                    }
                />
            </Route>
            {/* Catch-all 404.  Wrapped in DashboardLayout so the user
                still sees the nav/footer (and the support email) — a
                bare ``Navigate to="/"`` was a silent UX dead-end. */}
            <Route element={<DashboardLayout />}>
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Routes>
        </Suspense></WorkspaceProvider>
    );
}
